// Admin console (Phase 6, F-ADMIN-*) — the MANAGE-tier view. Four sections:
//
//   1. ACCESS CONTROL  — what deactivation actually does on THIS database,
//                        probed, not asserted (R-078 honesty).
//   2. TEAM & ROLES    — the staff directory, the role editor, and the
//                        deactivate / reactivate toggle with its confirm modal.
//   3. AUDIT TRAIL     — audit_events, filtered, newest first, in-app only.
//   4. ERROR LOG       — the errors.js ring buffer (R-017 completion): the
//                        founder can take a ref code over the phone and see it.
//
// ── WHAT THIS VIEW REFUSES TO DO ──────────────────────────────────────────
// * It does not null cases.assigned_to when an account is deactivated. The
//   caseload is SHOWN and the admin is sent to reassign; the app never silently
//   un-owns a patient (data/admin.js rule 2).
// * It does not export the audit trail. Audit summaries name patients
//   ("assigned Faith Njoki to Jane"), so the trail is a clinical record. It is
//   readable in-app by admins and goes no further — v_20 had no audit export
//   either, and adding a download here would be a new PII egress route that no
//   DPA covers.
// * It does not paint a green "Row-level security enforced ✓" tick. v_20's
//   "System posture" card hard-coded three ✓s that were never checked against
//   anything; §1 replaces them with one probed, three-state answer.
//
// Every dynamic string goes through esc(); every control is data-action (strict
// CSP, no inline handlers and no inline style attributes).

import { store, SLICES } from '../state/store.js';
import { esc } from '../ui/esc.js';
import { register } from '../ui/actions.js';
import { announce } from '../ui/announce.js';
import { registerView } from './registry.js';
import { initials } from './shell.js';
import { openWorkspace } from './workspace.js';
import { openModal, closeModal, setBtnBusy } from '../ui/modal.js';
import { toast } from '../ui/toast.js';
import { netErr, readErrorLog, RING_CAP } from '../errors.js';
import { isAdmin } from '../auth.js';
import { loadUsers, userName } from '../data/users.js';
import { openRegistryForOwner } from './patients.js';
import {
  AUDIT_TYPES,
  AUDIT_PAGE,
  AUDIT_MAX,
  EMAIL_KEYS,
  LAST_SEEN_KEYS,
  roleOptionsFor,
  roleConsequence,
  firstField,
  eventLabel,
  detailChips,
  searchAudit,
  loadTeam,
  caseloadFor,
  probeRlsEnforcement,
  loadAuditEvents,
  saveUserRole,
  setUserActive,
  lastAdminGuard,
} from '../data/admin.js';
import { logAudit, AUDIT_EVENT } from '../data/audit.js';
import { loadSignIns, signInTotals } from '../data/logins.js';
import {
  listFactors,
  enrollTotp,
  verifyCode,
  unenrollFactor,
  isValidCode,
  securityStatus,
  probeAdminUnenroll,
  adminRemoveFactor,
  MFA_RECOVERY_COPY,
  MFA_MANUAL_RECOVERY,
} from '../data/mfa.js';

// ─── Pure copy builders (unit-tested — the honesty lives here) ─────────────

/**
 * The one-line status for §1, from the probe result. THREE outcomes, because
 * "we could not check" is not the same claim as "it is not applied" and both
 * are different from "it is applied".
 * @param {{checked?: boolean, applied?: boolean}|null} probe
 * @returns {{cls: 'ok'|'warn'|'unknown', label: string, text: string}}
 */
export function enforcementStatus(probe) {
  if (!probe) {
    return {
      cls: 'unknown',
      label: 'Checking…',
      text: 'Asking the database whether deactivation is enforced server-side.',
    };
  }
  if (!probe.checked) {
    return {
      cls: 'unknown',
      label: 'Could not check',
      text:
        'The check itself failed, so we do not know whether server-side enforcement is applied. ' +
        'Assume it is NOT until you have confirmed it. Deactivating still removes app access at next login or refresh.',
    };
  }
  if (probe.applied) {
    return {
      cls: 'ok',
      label: 'Enforced server-side',
      text:
        'This database enforces app_users.is_active in its row-level security policies. ' +
        'Deactivating an account cuts off patient data immediately — including any session already signed in, and including direct API access outside this app.',
    };
  }
  return {
    cls: 'warn',
    label: 'App-level only',
    text:
      'This database does NOT yet enforce app_users.is_active in its security policies (migration 20260719120000 is not applied). ' +
      'Deactivating removes access at the next login or page refresh, but a session that is already signed in could still reach patient data directly through the API until the migration is applied. Treat deactivation as an interim control.',
  };
}

/**
 * The body copy for the deactivate/reactivate confirm modal. Written so that
 * EVERY sentence is true under all three enforcement states — the state-specific
 * claim comes from enforcementStatus() and nothing above it over-promises.
 * @param {{name: string, active: boolean, caseload: {open: number, total: number}|null, caseloadFailed?: boolean, probe: any}} ctx
 * @returns {string} HTML (all interpolations pre-escaped by the caller's esc())
 */
export function offboardCopy(ctx) {
  const name = esc(ctx.name);
  const st = enforcementStatus(ctx.probe);
  if (ctx.active) {
    return (
      `<p class="adm-p">Restore app access for <strong>${name}</strong>?</p>` +
      '<p class="adm-p">They will be able to sign in again and will reappear in assignment pickers (unless their role is admin, which never owns patients).</p>'
    );
  }
  let caseloadHTML;
  if (ctx.caseloadFailed) {
    caseloadHTML =
      '<p class="adm-p adm-warn"><strong>We could not read this user’s caseload.</strong> ' +
      'Check the registry before deactivating — if they still own patients, those patients will sit in no active nurse’s queue.</p>';
  } else if (ctx.caseload && ctx.caseload.open > 0) {
    const n = ctx.caseload.open;
    caseloadHTML =
      `<p class="adm-p adm-warn"><strong>${name} owns ${n} open patient${n === 1 ? '' : 's'} — reassign them first.</strong> ` +
      'Deactivating does <em>not</em> un-assign anyone: those patients keep this owner and will appear in no active nurse’s queue until someone reassigns them.</p>' +
      `<p class="adm-p"><button type="button" class="btn sm" data-action="adm-goto-caseload">Open the registry filtered to ${name}</button></p>`;
  } else {
    caseloadHTML = `<p class="adm-p">${name} owns no open patients, so no caseload needs reassigning.</p>`;
  }
  return (
    `<p class="adm-p">Remove app access for <strong>${name}</strong>?</p>` +
    caseloadHTML +
    '<p class="adm-p"><strong>What always happens:</strong> they are signed out at their next page load and cannot sign in again. ' +
    'Their name, their audit history and their past assignments are kept — nothing is deleted.</p>' +
    `<p class="adm-p adm-state adm-${st.cls}"><strong>On this database:</strong> ${esc(st.text)}</p>`
  );
}

/**
 * Summary line for the error-log panel. Pure.
 * @param {any[]} entries
 * @returns {string}
 */
export function errorLogSummary(entries) {
  const n = (entries || []).length;
  if (!n) return 'No errors recorded on this device. Refs appear here as soon as something fails.';
  return (
    `${n} error${n === 1 ? '' : 's'} recorded on this device (most recent last, capped at ${RING_CAP}). ` +
    'Refs, timestamps and codes only — no patient data is ever written to this log.'
  );
}

/**
 * The "copy refs" clipboard payload: one ref per line with its timestamp and
 * code. Pure — no PII by construction (the ring buffer holds no payloads).
 * @param {any[]} entries
 * @returns {string}
 */
export function refsText(entries) {
  return (entries || [])
    .map((e) => [e.ref || '—', e.at || '', e.code || '', e.source || ''].filter(Boolean).join('  '))
    .join('\n');
}

// ─── Module state ──────────────────────────────────────────────────────────
// Deliberately module-local rather than a store slice: nothing outside this view
// reads it, and P5's merge trap (a stale field surviving an error patch) is
// avoided by assigning whole objects and NULLING data on every failure.

let _wired = false;
/** @type {any[]|null} */
let _team = null;
let _teamError = false;
/** @type {any[]|null} */
let _audit = null;
let _auditError = false;
let _auditCapped = false;
let _auditLimit = AUDIT_PAGE;
/** @type {any} */
let _probe = null;
let _filters = { type: 'all', actor: 'all', from: '', to: '', q: '' };
// The user the OPEN offboard modal is about — read by the "open the registry
// filtered to X" button inside it. Held here rather than on the button so the
// link cannot be pointed at a different user by a stale DOM node.
let _pendingCaseloadUid = '';
let _pendingCaseloadName = '';
// R-021. `_factors` is THIS account's factor list (the client SDK can read no
// other); `_mfaAdminProbe` is whether the server-side removal function exists.
/** @type {any} */
let _factors = null;
let _factorsError = false;
/** @type {{checked: boolean, available: boolean}|null} */
let _mfaAdminProbe = null;

/** Test-only: reset module state. @returns {void} */
export function _resetAdmin() {
  _wired = false;
  _team = null;
  _teamError = false;
  _audit = null;
  _auditError = false;
  _auditCapped = false;
  _auditLimit = AUDIT_PAGE;
  _probe = null;
  _filters = { type: 'all', actor: 'all', from: '', to: '', q: '' };
  _pendingCaseloadUid = '';
  _pendingCaseloadName = '';
  _factors = null;
  _factorsError = false;
  _mfaAdminProbe = null;
}

function doc() {
  return /** @type {any} */ (globalThis).document;
}
function mounted() {
  const d = doc();
  const ui = store.get(SLICES.UI) || {};
  return !!d && ui.view === 'admin' && !!d.getElementById('admin-view');
}
function session() {
  return store.get(SLICES.SESSION) || {};
}
function role() {
  return session().role;
}
function selfId() {
  const s = session();
  return s.user && s.user.id;
}

// ─── §1 Access control ─────────────────────────────────────────────────────

function renderEnforcement() {
  const d = doc();
  const host = d && d.getElementById('adm-enforce');
  if (!host) return;
  const st = enforcementStatus(_probe);
  host.innerHTML =
    `<div class="adm-state adm-${st.cls}">` +
    `<span class="adm-badge adm-${st.cls}">${esc(st.label)}</span>` +
    `<p>${esc(st.text)}</p>` +
    '</div>' +
    '<p class="adm-note">Deactivation is the interim offboarding control (R-078). There is no self-service password reset and no ' +
    'account deletion here — an account that has touched patient records is deactivated, never erased, so the audit trail stays readable.</p>';
}

// ─── §1b Security — TOTP MFA for YOUR OWN account (R-021) ──────────────────
//
// OPT-IN, and the shape of this card is what makes that true: it describes one
// account (the signed-in one), it has an "off" state that is not styled as a
// problem, and there is no path from here to enabling MFA for anybody else.
// The only cross-account control is the removal path at the bottom, which
// exists to UNDO a lockout, never to impose one.

function renderSecurity() {
  const d = doc();
  const host = d && d.getElementById('adm-sec');
  if (!host) return;
  const st = securityStatus(_factors, _factorsError);
  // NO GREEN TICK for 'on'. The 6-digit prompt is drawn by this app after
  // signInWithPassword() has already resolved, so the session exists before the
  // code is asked for and no RLS policy requires aal2 — see MFA_SCOPE_COPY.
  // §1 above refuses to paint a tick for enforcement it has not probed; this
  // card does not get to paint one for a control that is purely client-side.
  const cls = st.state === 'unknown' ? 'unknown' : st.state === 'on' ? 'neutral' : 'warn';

  let actions;
  if (st.state === 'unknown') {
    actions = '<button type="button" class="btn sm" data-action="adm-mfa-refresh">Try again</button>';
  } else if (st.state === 'on') {
    actions =
      '<button type="button" class="btn sm danger" data-action="adm-mfa-remove-self" ' +
      `data-factor="${esc(st.factorId || '')}">Turn off two-step verification</button>`;
  } else if (st.state === 'half') {
    actions =
      '<button type="button" class="btn sm danger" data-action="adm-mfa-remove-self" ' +
      `data-factor="${esc(st.factorId || '')}">Remove the unfinished setup</button>` +
      '<button type="button" class="btn sm pri" data-action="adm-mfa-enrol">Start again</button>';
  } else {
    actions = '<button type="button" class="btn sm pri" data-action="adm-mfa-enrol">Set up two-step verification</button>';
  }

  host.innerHTML =
    `<div class="adm-state adm-${cls}">` +
    `<span class="adm-badge adm-${cls}">${esc(st.label)}</span>` +
    `<p>${esc(st.text)}</p>` +
    '</div>' +
    // The opt-in promise, written down where the person deciding can read it.
    '<p class="adm-note">This setting covers your account only. Tumaini never requires two-step verification and never ' +
    'blocks a colleague who has not set it up — turning it on here changes nothing for anyone else.</p>' +
    `<p class="adm-note adm-note-warn">${esc(MFA_RECOVERY_COPY)}</p>` +
    `<div class="adm-sec-acts">${actions}</div>` +
    renderAdminRecovery()
  ;
}

/** The lockout-recovery sub-panel. Admin-only, and honest about whether the
 *  server-side path this needs actually exists on THIS database. */
function renderAdminRecovery() {
  if (!isAdmin(role())) return '';
  const p = _mfaAdminProbe;
  let body;
  if (!p) {
    body = '<p class="adm-note">Checking whether this database can remove another user’s two-step verification…</p>';
  } else if (!p.checked) {
    body =
      '<p class="adm-note adm-note-warn">The check itself failed, so we do not know whether recovery works from this screen. ' +
      'Assume it does not until you have confirmed it.</p>';
  } else if (!p.available) {
    body = `<p class="adm-note adm-note-warn">${esc(MFA_MANUAL_RECOVERY)}</p>`;
  } else {
    const opts = (_team || [])
      .filter((u) => u && u.user_id && u.user_id !== selfId())
      .map((u) => `<option value="${esc(String(u.user_id))}">${esc(u.full_name || u.user_id)}</option>`)
      .join('');
    body =
      '<p class="adm-note">Removing someone’s factor lets them sign in with their password alone until they set it up ' +
      'again. We cannot see from here whether a given colleague has two-step verification on — this account’s settings are ' +
      'the only ones readable by this app — so run it when someone tells you they are locked out.</p>' +
      '<div class="adm-sec-acts">' +
      `<label class="adm-lab" for="adm-mfa-user">Colleague</label>` +
      `<select id="adm-mfa-user" class="adm-sel">${opts || '<option value="">No other staff loaded</option>'}</select>` +
      '<button type="button" class="btn sm danger" data-action="adm-mfa-remove-other">Remove their two-step verification</button>' +
      '</div>';
  }
  return (
    '<div class="adm-sec-rec">' +
    '<h3 class="adm-sec-h">Locked out? (administrators)</h3>' +
    body +
    '</div>'
  );
}

/** The enrolment modal: QR when we can render one, secret + otpauth URI always. */
function showEnrolModal(data) {
  const totp = (data && data.totp) || {};
  const factorId = (data && data.id) || '';
  // Per-enrolment, not module-level: this object lives exactly as long as the
  // dialog that owns it, so no previous successful setup can speak for it.
  const enrol = { confirmed: false };
  // The QR is a data: URI SVG from Supabase. img-src in the shipped CSP allows
  // `data:`, so this renders without a QR library — we vendor NOTHING new for
  // this. When it is missing or the image fails, the secret and the otpauth URI
  // below are a complete, sufficient path on their own: every authenticator app
  // accepts a typed secret.
  const qr = totp.qr_code
    ? `<div class="adm-mfa-qr"><img src="${esc(String(totp.qr_code))}" alt="QR code for setting up two-step verification" width="180" height="180"/></div>`
    : '<p class="adm-note adm-note-warn">This server did not return a QR image — use the setup key below, which works the same way.</p>';

  openModal(
    'Set up two-step verification',
    'Scan the code with an authenticator app, then confirm with a 6-digit code.',
    qr +
      '<ol class="adm-mfa-steps">' +
      '<li>Install an authenticator app if you do not have one (Google Authenticator, Microsoft Authenticator, 1Password, Aegis).</li>' +
      '<li>Scan the code above, or add an account manually with the setup key below.</li>' +
      '<li>Type the 6-digit code it shows, to confirm it works.</li>' +
      '</ol>' +
      '<div class="field"><label for="adm-mfa-secret">Setup key (if you cannot scan)</label>' +
      `<input id="adm-mfa-secret" class="adm-inp" readonly value="${esc(String(totp.secret || ''))}"/></div>` +
      '<div class="field"><label for="adm-mfa-uri">Full setup link</label>' +
      `<input id="adm-mfa-uri" class="adm-inp" readonly value="${esc(String(totp.uri || ''))}"/></div>` +
      `<p class="adm-note adm-note-warn">${esc(MFA_RECOVERY_COPY)}</p>` +
      '<div class="field"><label for="adm-mfa-code">6-digit code from the app</label>' +
      '<input id="adm-mfa-code" class="adm-inp" inputmode="numeric" autocomplete="one-time-code" maxlength="7" placeholder="123456"/></div>' +
      '<div id="adm-mfa-err" class="adm-mfa-err" role="alert"></div>',
    [
      { l: 'Cancel', v: 'ghost', k: 'cancel', fn: () => closeModal() },
      { l: 'Confirm', v: 'pri', k: 'confirm', fn: () => void onConfirmEnrol(factorId, enrol) },
    ],
    false,
    // Cleanup on EVERY dismissal route, not just the Cancel button. main.js
    // binds Escape straight to closeModal(), so routing the unenroll through
    // the button alone left the half-created factor on the account whenever
    // somebody dismissed the dialog the normal way.
    { onClose: () => void onCancelEnrol(factorId, enrol) }
  );
}

/**
 * Pure: does closing this dialog leave a factor to clean up?
 *
 * The state is created PER ENROLMENT (see showEnrolModal) and captured by both
 * hooks, so it cannot go stale. A module-level flag could not say that: once a
 * confirm succeeded it stayed true, and the NEXT enrolment — set up again after
 * turning MFA off — skipped its cleanup entirely when dismissed with Escape or
 * Cancel, leaving the unverified factor on the account forever.
 * @param {{confirmed: boolean}|null} state
 * @returns {boolean}
 */
export function shouldCleanupEnrol(state) {
  return !(state && state.confirmed);
}

function mfaErr(msg) {
  const d = doc();
  const el = d && d.getElementById('adm-mfa-err');
  if (el) {
    // Reveal, clear, then fill on a later task — see ui/announce.js for why
    // the order AND the gap matter to a role="alert" region that is
    // display:none until shown.
    el.classList.add('show');
    announce(el, msg);
  }
}

async function onEnrol() {
  const s = session();
  const name = 'Tumaini · ' + (s.fullName || 'account');
  const res = await enrollTotp(name);
  if (!res.ok) {
    toast(netErr(res.error, 'Could not start two-step setup'), 'err');
    return;
  }
  showEnrolModal(res.data);
}

/** Cancel mid-enrolment: the factor Supabase just created is UNVERIFIED and
 *  would otherwise sit on the account forever as a confusing half-state. Clean
 *  it up, but never let a failed cleanup block closing the dialog. */
async function onCancelEnrol(factorId, enrol) {
  if (!shouldCleanupEnrol(enrol)) return; // verified factor — nothing to clean up
  if (factorId) {
    const res = await unenrollFactor(factorId);
    if (!res.ok) toast('Setup cancelled, but the unfinished factor could not be cleared', 'err');
  }
  await refreshFactors();
}

async function onConfirmEnrol(factorId, enrol) {
  const d = doc();
  const code = ((d && d.getElementById('adm-mfa-code')) || {}).value || '';
  if (!isValidCode(code)) {
    mfaErr('Enter the 6-digit code from your authenticator app.');
    return;
  }
  setBtnBusy(true, 'Checking…', 'confirm');
  const res = await verifyCode(factorId, code);
  if (!res.ok) {
    // KEEP-OPEN-ON-FAIL: the modal stays, the typed code stays visible, the
    // user tries the next code. A closed modal here would mean re-scanning.
    setBtnBusy(false);
    mfaErr(
      res.code === 'MFA_BAD_CODE'
        ? 'Enter the 6-digit code from your authenticator app.'
        : 'That code was not accepted. Codes change every 30 seconds — wait for the next one and try again.'
    );
    return;
  }
  if (enrol) enrol.confirmed = true; // read by the close hook — do NOT unenroll this one
  closeModal();
  toast('Two-step verification is on', 'ok');
  void logAudit(AUDIT_EVENT.DATA_CHANGED, 'turned on two-step verification for their own account', null, {
    area: 'security',
    action: 'mfa_enrolled',
  });
  await refreshFactors();
}

async function onRemoveSelf(el) {
  const factorId = el.getAttribute('data-factor') || '';
  uiConfirmMfa(
    'Turn off two-step verification?',
    'Your account will be protected by your password alone. You can set it up again at any time.',
    'Turn it off',
    async () => {
      const res = await unenrollFactor(factorId);
      if (!res.ok) {
        setBtnBusy(false);
        toast(netErr(res.error, 'Could not turn it off'), 'err');
        return; // modal stays open — the failed write changed nothing
      }
      closeModal();
      toast('Two-step verification is off', 'ok');
      void logAudit(AUDIT_EVENT.DATA_CHANGED, 'turned off two-step verification for their own account', null, {
        area: 'security',
        action: 'mfa_unenrolled',
      });
      await refreshFactors();
    }
  );
}

async function onRemoveOther() {
  if (!isAdmin(role())) return; // defence in depth; the real gate is in the DB function
  const d = doc();
  const sel = d && d.getElementById('adm-mfa-user');
  const uid = (sel && sel.value) || '';
  if (!uid) {
    toast('Choose whose two-step verification to remove', 'err');
    return;
  }
  const name = userName(uid) || 'this colleague';
  uiConfirmMfa(
    'Remove two-step verification?',
    `${name} will be able to sign in with their password alone until they set it up again. ` +
      'This is recorded in the audit trail with your name on it.',
    'Remove it',
    async () => {
      const res = await adminRemoveFactor(uid);
      if (!res.ok) {
        setBtnBusy(false);
        toast(
          res.code === 'MFA_ADMIN_RPC_ABSENT'
            ? 'This database has no admin removal function — use the Supabase dashboard'
            : netErr(res.error, 'Could not remove it'),
          'err'
        );
        return;
      }
      closeModal();
      toast('Two-step verification removed for ' + name, 'ok');
      void logAudit(
        AUDIT_EVENT.DATA_CHANGED,
        `removed two-step verification for ${name} (lockout recovery)`,
        null,
        { area: 'security', action: 'mfa_admin_removed', target_user_id: uid }
      );
    }
  );
}

/** A small confirm over the shared modal (native confirm() is banned, R-015). */
function uiConfirmMfa(title, body, okLabel, onOk) {
  openModal(title, '', `<p class="adm-p">${esc(body)}</p>`, [
    { l: 'Cancel', v: 'ghost', k: 'cancel', fn: () => closeModal() },
    {
      l: okLabel,
      v: 'pri danger',
      k: 'ok',
      fn: () => {
        setBtnBusy(true, 'Working…', 'ok');
        void onOk();
      },
    },
  ]);
}

async function refreshFactors() {
  const res = await listFactors();
  // P5: an error path must NULL the stale data, never leave the last good
  // answer on screen under a failure. "Two-step verification is on" left over
  // from a previous read would be a security claim we no longer have evidence for.
  _factors = res.ok ? res.data : null;
  _factorsError = !res.ok;
  if (!res.ok) netErr(res.error, 'Could not read your security settings');
  renderSecurity();
}

// ─── §2 Team & roles ───────────────────────────────────────────────────────

function teamRowHTML(u, cols) {
  const uid = String(u.user_id || '');
  const name = u.full_name || '—';
  const cur = u.role || 'nurse';
  const active = u.is_active !== false;
  const isSelf = uid === selfId();
  const cons = roleConsequence(cur);
  const opts = roleOptionsFor(cur)
    .map((r) => `<option value="${esc(r)}"${r === cur ? ' selected' : ''}>${esc(r)}</option>`)
    .join('');
  const email = cols.email ? firstField(u, EMAIL_KEYS) : null;
  const seen = cols.seen ? firstField(u, LAST_SEEN_KEYS) : null;

  return (
    `<div class="adm-row${active ? '' : ' inactive'}">` +
    '<div class="adm-who">' +
    `<span class="adm-av" aria-hidden="true">${esc(initials(name))}</span>` +
    '<span class="adm-who-t">' +
    `<span class="adm-name">${esc(name)}${isSelf ? ' <span class="adm-you">you</span>' : ''}</span>` +
    `<span class="adm-sub">${active ? 'Active' : 'No access'} · ${esc(cur)}` +
    (email ? ' · ' + esc(email) : '') +
    (seen ? ' · last seen ' + esc(fmtWhen(seen)) : '') +
    '</span>' +
    `<span class="adm-cons">${esc(cons.text)}</span>` +
    '</span></div>' +
    '<div class="adm-role">' +
    `<label class="adm-lab" for="adm-role-${esc(uid)}">Role</label>` +
    `<select id="adm-role-${esc(uid)}" class="adm-sel" data-action="adm-role-change" data-uid="${esc(uid)}"${active ? '' : ' disabled'}>${opts}</select>` +
    `<button type="button" class="btn sm" data-action="adm-role-save" data-uid="${esc(uid)}" data-name="${esc(name)}" data-prev="${esc(cur)}"${active ? '' : ' disabled'}>Save</button>` +
    '</div>' +
    '<div class="adm-acc">' +
    `<button type="button" class="btn sm ${active ? 'danger' : 'pri'}" data-action="adm-toggle-active" ` +
    `data-uid="${esc(uid)}" data-name="${esc(name)}" data-active="${active ? '1' : '0'}"${isSelf ? ' disabled title="You cannot remove your own access"' : ''}>` +
    `${active ? 'Deactivate' : 'Reactivate'}</button>` +
    '</div>' +
    '</div>'
  );
}

function fmtWhen(v) {
  const t = new Date(v);
  if (isNaN(t.getTime())) return String(v);
  return t.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function renderTeam() {
  const d = doc();
  const host = d && d.getElementById('adm-team');
  if (!host) return;
  if (_teamError) {
    host.innerHTML =
      '<button type="button" class="today-error" data-action="adm-team-retry">' +
      '<div><h3>Couldn’t load the team — tap to retry</h3>' +
      '<p>The staff directory did not load, so no roles are shown. Nothing here is a statement about who has access.</p></div></button>';
    return;
  }
  if (_team === null) {
    host.innerHTML = '<div class="skel"></div><div class="skel"></div><div class="skel"></div>';
    return;
  }
  if (!_team.length) {
    host.innerHTML = '<div class="adm-empty"><h3>No staff accounts</h3><p>Accounts appear here once they exist in app_users.</p></div>';
    return;
  }
  // Which optional columns this deployment actually has (see EMAIL_KEYS note in
  // data/admin.js): render a field only when a row really carries it.
  const cols = {
    email: _team.some((u) => firstField(u, EMAIL_KEYS)),
    seen: _team.some((u) => firstField(u, LAST_SEEN_KEYS)),
  };
  const nActive = _team.filter((u) => u.is_active !== false).length;
  const meta = d.getElementById('adm-team-meta');
  if (meta) meta.textContent = `${nActive} active · ${_team.length} total`;
  host.innerHTML = _team.map((u) => teamRowHTML(u, cols)).join('');
}

async function refreshTeam() {
  const res = await loadTeam();
  if (!res.ok) {
    // P5 lesson: an error path must NULL the stale data, never leave the last
    // good list on screen under an error banner.
    _team = null;
    _teamError = true;
    const d = doc();
    const meta = d && d.getElementById('adm-team-meta');
    if (meta) meta.textContent = 'Team not loaded';
    renderTeam();
    toast(netErr(res.error, 'Could not load the team'), 'err');
    // netErr just wrote a ref into the ring buffer. Re-render §4 or the founder
    // reads "No errors recorded" on the very screen that just failed.
    renderErrors();
    return;
  }
  _teamError = false;
  _team = res.data;
  renderTeam();
  renderActorFilter();
}

async function onRoleSave(el) {
  const uid = el.getAttribute('data-uid');
  const name = el.getAttribute('data-name') || '';
  const prev = el.getAttribute('data-prev') || null;
  if (!uid) return;
  const d = doc();
  const sel = d && d.getElementById('adm-role-' + uid);
  const next = sel ? String(/** @type {any} */ (sel).value || '') : '';
  if (!next) return;
  if (next === prev) {
    toast('No change to save');
    return;
  }
  // Refuse the orphaning change BEFORE the modal, so the confirm dialog never
  // offers a choice the write is going to reject anyway.
  const orphan = lastAdminGuard(uid, { role: next }, _team);
  if (orphan) {
    openModal('Cannot change this role', name, `<p class="adm-note adm-note-warn">${esc(orphan)}</p>`, [
      { l: 'OK', v: 'pri', k: 'ok', fn: () => closeModal() },
    ]);
    return;
  }

  // Changing your OWN role is the one role write that can log you out of the
  // console, so it gets the same confirm treatment deactivation already has.
  if (uid === selfId()) {
    openModal(
      'Change your own role?',
      name,
      '<p class="adm-note adm-note-warn">You are changing YOUR OWN role, from ' +
        `${esc(String(prev || 'unknown'))} to ${esc(next)}. ` +
        (isAdmin(next)
          ? 'You keep the Admin console.'
          : 'You will lose the Admin console at your next page load, and only another administrator can give it back.') +
        '</p>',
      [
        { l: 'Cancel', k: 'cancel', fn: () => closeModal() },
        { l: 'Change my role', v: 'danger', k: 'go', fn: () => void commitRoleSave(el, uid, name, next, prev, true) },
      ]
    );
    return;
  }
  await commitRoleSave(el, uid, name, next, prev, false);
}

/**
 * The role write itself. Split out so the self-change path can route through a
 * confirm modal and still share one success/failure story.
 * @param {Element} el the Save button
 * @param {string} uid
 * @param {string} name
 * @param {string} next
 * @param {string|null} prev
 * @param {boolean} viaModal true when a confirm modal is open and owns the busy state
 * @returns {Promise<void>}
 */
async function commitRoleSave(el, uid, name, next, prev, viaModal) {
  const btn = /** @type {any} */ (el);
  const label = btn.textContent;
  if (viaModal) setBtnBusy(true, 'Saving…', 'go');
  else {
    btn.disabled = true;
    btn.textContent = 'Saving…';
  }
  const res = await saveUserRole(uid, name, next, prev, { role: role(), team: _team });
  if (viaModal) setBtnBusy(false);
  else {
    btn.disabled = false;
    btn.textContent = label;
  }
  if (!res.ok) {
    // Modal stays OPEN on failure (house rule).
    if (res.code === 'FORBIDDEN' || res.code === 'LAST_ADMIN') toast((res.error && res.error.message) || 'Not allowed', 'err');
    else if (res.code !== 'NO_CHANGE') toast(netErr(res.error, 'Could not update the role'), 'err');
    return;
  }
  if (viaModal) closeModal();
  const cons = roleConsequence(next);
  toast(`${name} is now ${next} — ${cons.assign ? 'can assign patients' : 'cannot bulk-assign'}, ${cons.ownsPatients ? 'can own patients' : 'cannot own patients'}`, 'ok');
  await loadUsers(); // the app-wide directory drives every assignment picker
  await refreshTeam();
  void refreshAudit();
}

/**
 * The deactivate / reactivate confirm. Reads the caseload FIRST so the warning
 * is a real number, and keeps the modal open on failure (house rule).
 * @param {Element} el
 * @returns {Promise<void>}
 */
async function onToggleActive(el) {
  const uid = el.getAttribute('data-uid');
  const name = el.getAttribute('data-name') || 'this user';
  const wasActive = el.getAttribute('data-active') === '1';
  if (!uid) return;
  if (uid === selfId()) {
    toast('You cannot remove your own access', 'err');
    return;
  }
  const target = !wasActive; // the state we are moving TO

  /** @type {{open: number, total: number}|null} */
  let caseload = null;
  let caseloadFailed = false;
  if (wasActive) {
    const cl = await caseloadFor(uid);
    if (cl.ok) caseload = cl.data;
    else caseloadFailed = true; // surfaced in the copy — never assumed to be zero
  }

  _pendingCaseloadUid = uid;
  _pendingCaseloadName = name;

  openModal(
    target ? 'Restore access' : 'Remove access',
    name,
    offboardCopy({ name, active: target, caseload, caseloadFailed, probe: _probe }),
    [
      { l: 'Cancel', k: 'cancel', fn: () => closeModal() },
      {
        l: target ? 'Reactivate' : 'Deactivate',
        v: target ? 'pri' : 'danger',
        k: 'go',
        fn: () => void confirmToggle(uid, name, target),
      },
    ]
  );
}

async function confirmToggle(uid, name, target) {
  setBtnBusy(true, 'Saving…', 'go');
  const res = await setUserActive(uid, name, target, { role: role(), team: _team });
  if (!res.ok) {
    setBtnBusy(false);
    // Modal stays OPEN on failure (house rule) — the admin sees what failed.
    if (res.code === 'FORBIDDEN' || res.code === 'LAST_ADMIN') toast((res.error && res.error.message) || 'Not allowed', 'err');
    else toast(netErr(res.error, target ? 'Could not restore access' : 'Could not remove access'), 'err');
    return;
  }
  closeModal();
  toast(target ? `${name} can sign in again` : `${name} has no app access from their next page load`, 'ok');
  await loadUsers();
  await refreshTeam();
  void refreshAudit();
}

// ─── §3 Audit trail ────────────────────────────────────────────────────────

function renderActorFilter() {
  const d = doc();
  const sel = d && d.getElementById('adm-aud-actor');
  if (!sel) return;
  const cur = _filters.actor;
  const people = (_team || []).slice();
  sel.innerHTML =
    '<option value="all">Anyone</option>' +
    people
      .map((u) => `<option value="${esc(u.user_id)}"${u.user_id === cur ? ' selected' : ''}>${esc(u.full_name || u.user_id)}</option>`)
      .join('');
}

function auditRowHTML(e) {
  const who = e.actor_user_id ? userName(e.actor_user_id) : (e.details && e.details.by) || null;
  const chips = detailChips(e.details);
  return (
    '<div class="adm-aud-row">' +
    `<span class="adm-aud-type">${esc(eventLabel(e.event_type))}</span>` +
    '<span class="adm-aud-main">' +
    `<span class="adm-aud-sum">${esc(e.summary || eventLabel(e.event_type))}</span>` +
    (chips.length
      ? '<span class="adm-chips">' + chips.map((c) => `<span class="adm-chip">${esc(c.k)}: ${esc(c.v)}</span>`).join('') + '</span>'
      : '') +
    '</span>' +
    `<span class="adm-aud-meta">${esc(fmtWhen(e.occurred_at))}${who ? ' · by ' + esc(who) : ''}</span>` +
    (e.case_id
      ? `<button type="button" class="btn sm ghost" data-action="adm-aud-open" data-case="${esc(e.case_id)}">Patient</button>`
      : '<span class="adm-aud-nolink"></span>') +
    '</div>'
  );
}

function renderAudit() {
  const d = doc();
  const host = d && d.getElementById('adm-aud-list');
  const meta = d && d.getElementById('adm-aud-meta');
  const more = d && d.getElementById('adm-aud-more');
  if (!host) return;
  if (_auditError) {
    host.innerHTML =
      '<button type="button" class="today-error" data-action="adm-aud-retry">' +
      '<div><h3>Couldn’t load the audit trail — tap to retry</h3>' +
      '<p>The query failed or was denied. An empty list here would look like "nothing happened", which is why this is shown instead.</p></div></button>';
    if (meta) meta.textContent = 'Audit trail not loaded';
    if (more) more.innerHTML = '';
    return;
  }
  if (_audit === null) {
    host.innerHTML = '<div class="skel"></div><div class="skel"></div><div class="skel"></div>';
    if (meta) meta.textContent = 'Loading…';
    if (more) more.innerHTML = '';
    return;
  }
  const rows = searchAudit(_audit, _filters.q);
  if (meta) {
    meta.textContent =
      `${rows.length} shown` +
      (_filters.q.trim() ? ` of ${_audit.length} loaded` : '') +
      (_auditCapped ? ` · capped at the ${_auditLimit} most recent — narrow the filters to reach older events` : '');
  }
  if (!rows.length) {
    host.innerHTML =
      '<div class="adm-empty"><h3>No events match these filters</h3>' +
      '<p>This is a statement about the filters, not about the system — widen the date range or clear the search.</p></div>';
    if (more) more.innerHTML = '';
    return;
  }
  host.innerHTML = rows.map(auditRowHTML).join('');
  if (more) {
    more.innerHTML =
      _auditCapped && _auditLimit < AUDIT_MAX
        ? `<button type="button" class="btn" data-action="adm-aud-more">Load ${AUDIT_PAGE} more</button>`
        : '';
  }
}

async function refreshAudit() {
  const res = await loadAuditEvents({
    type: _filters.type,
    actor: _filters.actor,
    from: _filters.from,
    to: _filters.to,
    limit: _auditLimit,
  });
  if (!res.ok) {
    _audit = null; // null the stale rows — see P5
    _auditError = true;
    _auditCapped = false;
    renderAudit();
    toast(netErr(res.error, 'Could not load the audit trail'), 'err');
    renderErrors(); // the fresh ref must appear in §4 now, not after a Refresh
    return;
  }
  _auditError = false;
  _audit = res.data.rows;
  _auditCapped = res.data.capped;
  renderAudit();
}

// ─── §4 Error log (R-017) ──────────────────────────────────────────────────

function renderErrors() {
  const d = doc();
  const host = d && d.getElementById('adm-err-list');
  const meta = d && d.getElementById('adm-err-meta');
  if (!host) return;
  const entries = readErrorLog();
  if (meta) meta.textContent = errorLogSummary(entries);
  if (!entries.length) {
    host.innerHTML = '<div class="adm-empty"><h3>No errors recorded</h3><p>If a nurse reports a ref code, it will be here on their device.</p></div>';
    return;
  }
  host.innerHTML = entries
    .slice()
    .reverse()
    .map(
      (e) =>
        '<div class="adm-err-row">' +
        `<span class="adm-ref mono">${esc(e.ref || '—')}</span>` +
        `<span class="adm-err-when">${esc(e.at ? fmtWhen(e.at) : '—')}</span>` +
        `<span class="adm-err-src">${esc(e.source || '—')}${e.code ? ' · ' + esc(e.code) : ''}</span>` +
        `<span class="adm-err-msg">${esc(e.message || e.name || '—')}</span>` +
        '</div>'
    )
    .join('');
}

function onCopyRefs() {
  const entries = readErrorLog();
  if (!entries.length) {
    toast('Nothing to copy — no errors recorded on this device');
    return;
  }
  const text = refsText(entries);
  const nav = /** @type {any} */ (globalThis).navigator;
  if (nav && nav.clipboard && nav.clipboard.writeText) {
    nav.clipboard.writeText(text).then(
      () => toast(`${entries.length} ref${entries.length === 1 ? '' : 's'} copied`, 'ok'),
      // Never a silent success: a denied clipboard permission must say so.
      () => toast('Could not copy — your browser blocked clipboard access', 'err')
    );
    return;
  }
  toast('Clipboard is unavailable in this browser', 'err');
}

// ─── Wiring ────────────────────────────────────────────────────────────────

function wireOnce() {
  if (_wired) return;
  _wired = true;

  register('adm-team-retry', () => void refreshTeam());
  register('adm-role-save', (el) => void onRoleSave(el));
  register('adm-toggle-active', (el) => void onToggleActive(el));
  register('adm-goto-caseload', () => {
    // Fired from inside the confirm modal: close it, then focus the registry on
    // this owner. Nothing has been written at this point.
    const uid = _pendingCaseloadUid;
    closeModal();
    if (uid) openRegistryForOwner(uid, _pendingCaseloadName);
  });
  register('adm-aud-retry', () => void refreshAudit());
  register('adm-aud-more', () => {
    _auditLimit = Math.min(_auditLimit + AUDIT_PAGE, AUDIT_MAX);
    void refreshAudit();
  });
  register('adm-aud-open', (el) => {
    const id = el.getAttribute('data-case');
    if (id) void openWorkspace(id);
  });
  register('adm-mfa-enrol', () => void onEnrol());
  register('adm-mfa-refresh', () => void refreshFactors());
  register('adm-mfa-remove-self', (el) => void onRemoveSelf(el));
  register('adm-mfa-remove-other', () => void onRemoveOther());
  register('adm-err-copy', () => onCopyRefs());
  register('adm-err-refresh', () => renderErrors());

  const d = doc();
  if (d) {
    d.addEventListener('change', (ev) => {
      const t = ev.target;
      if (!t || !t.id || !mounted()) return;
      if (t.id === 'adm-aud-type') _filters = Object.assign({}, _filters, { type: t.value });
      else if (t.id === 'adm-aud-actor') _filters = Object.assign({}, _filters, { actor: t.value });
      else if (t.id === 'adm-aud-from') _filters = Object.assign({}, _filters, { from: t.value });
      else if (t.id === 'adm-aud-to') _filters = Object.assign({}, _filters, { to: t.value });
      else return;
      _auditLimit = AUDIT_PAGE; // a new filter starts a new page
      void refreshAudit();
    });
    d.addEventListener('input', (ev) => {
      const t = ev.target;
      if (!t || t.id !== 'adm-aud-q' || !mounted()) return;
      _filters = Object.assign({}, _filters, { q: t.value || '' });
      renderAudit(); // search narrows the LOADED page only (see searchAudit)
    });
  }
}

// ─── Skeleton + registration ───────────────────────────────────────────────

function skeleton() {
  const typeOpts =
    '<option value="all">All events</option>' +
    AUDIT_TYPES.map((t) => `<option value="${esc(t.v)}">${esc(t.l)}</option>`).join('');
  return (
    '<div id="admin-view" class="admin">' +
    '<header class="view-head"><h1>Admin</h1>' +
    '<p class="adm-lede">Team access, the audit trail, and the error log. Everything on this page is admin-only and stays in the app — ' +
    'audit summaries name patients, so there is no export here.</p></header>' +

    // §1
    '<section class="today-card adm-card" aria-labelledby="adm-h-access">' +
    '<div class="adm-chd"><h2 id="adm-h-access">Access control</h2></div>' +
    '<div id="adm-enforce" class="adm-bd"></div>' +
    '</section>' +

    // §1b — R-021. Deliberately its own card, directly under access control:
    // it is about ONE account (yours), and it must not read as a team policy.
    '<section class="today-card adm-card" aria-labelledby="adm-h-sec">' +
    '<div class="adm-chd"><div><h2 id="adm-h-sec">Security — your account</h2>' +
    '<p class="adm-sub-h">Optional two-step verification with an authenticator app.</p></div></div>' +
    '<div id="adm-sec" class="adm-bd"></div>' +
    '</section>' +

    // §2
    '<section class="today-card adm-card" aria-labelledby="adm-h-team">' +
    '<div class="adm-chd"><div><h2 id="adm-h-team">Team &amp; roles</h2>' +
    '<p class="adm-sub-h">Coordinators can assign patients to nurses. Admins manage the programme and cannot own patients — ' +
    'promoting someone to admin removes them from every assignment picker.</p></div>' +
    '<span id="adm-team-meta" class="adm-meta"></span></div>' +
    '<div id="adm-team" class="adm-list"></div>' +
    '</section>' +

    // §3
    '<section class="today-card adm-card" aria-labelledby="adm-h-aud">' +
    '<div class="adm-chd"><div><h2 id="adm-h-aud">Audit trail</h2>' +
    '<p class="adm-sub-h">Every recorded action, newest first.</p></div>' +
    '<span id="adm-aud-meta" class="adm-meta"></span></div>' +
    '<div class="adm-filters">' +
    `<label class="adm-f"><span>Event</span><select id="adm-aud-type" class="adm-sel">${typeOpts}</select></label>` +
    '<label class="adm-f"><span>Actor</span><select id="adm-aud-actor" class="adm-sel"><option value="all">Anyone</option></select></label>' +
    '<label class="adm-f"><span>From</span><input id="adm-aud-from" class="adm-inp" type="date"/></label>' +
    '<label class="adm-f"><span>To</span><input id="adm-aud-to" class="adm-inp" type="date"/></label>' +
    '<label class="adm-f adm-f-grow"><span>Search</span><input id="adm-aud-q" class="adm-inp" type="search" placeholder="Search the loaded events…"/></label>' +
    '</div>' +
    '<div id="adm-aud-list" class="adm-list"></div>' +
    '<div id="adm-aud-more" class="adm-more"></div>' +
    '</section>' +

    // §3b — sign-in audit (WHO entered the app, WHEN, from what device).
    '<section class="today-card adm-card" aria-labelledby="adm-h-signin">' +
    '<div class="adm-chd"><div><h2 id="adm-h-signin">Sign-in history</h2>' +
    '<p class="adm-sub-h">Every entry into the app — fresh sign-ins and resumed sessions, newest first. ' +
    'Cross-reference the audit trail above to see what was done in a session.</p></div>' +
    '<span id="adm-signin-meta" class="adm-meta"></span></div>' +
    '<div id="adm-signin-totals" class="adm-bd"></div>' +
    '<div id="adm-signin-list" class="adm-list"></div>' +
    '</section>' +

    // §4
    '<section class="today-card adm-card" aria-labelledby="adm-h-err">' +
    '<div class="adm-chd"><div><h2 id="adm-h-err">Error log (this device)</h2>' +
    '<p id="adm-err-meta" class="adm-sub-h"></p></div>' +
    '<span class="adm-hd-acts">' +
    '<button type="button" class="btn sm ghost" data-action="adm-err-refresh">Refresh</button>' +
    '<button type="button" class="btn sm" data-action="adm-err-copy">Copy refs</button>' +
    '</span></div>' +
    '<div id="adm-err-list" class="adm-list"></div>' +
    '</section>' +
    '</div>'
  );
}

// ─── §3b Sign-in history ───────────────────────────────────────────────────

/** @type {{rows: any[], error: any}|null} loaded once per Admin visit */
let _signins = null;

function renderSignIns() {
  const d = doc();
  const totals = d && d.getElementById('adm-signin-totals');
  const list = d && d.getElementById('adm-signin-list');
  const meta = d && d.getElementById('adm-signin-meta');
  if (!totals || !list) return;
  if (!_signins) {
    list.innerHTML = '<p class="adm-sub-h">Loading…</p>';
    return;
  }
  if (_signins.error) {
    totals.innerHTML = '';
    list.innerHTML =
      '<p class="adm-sub-h">Could not load sign-ins. If the app_logins migration (20260801210000) has not been run yet, run it first — until then no sign-ins are recorded.</p>';
    return;
  }
  const rows = _signins.rows;
  if (meta) meta.textContent = rows.length >= 200 ? 'latest 200' : `${rows.length} recorded`;
  const t = signInTotals(rows);
  totals.innerHTML = t.length
    ? '<div class="adm-si-tot">' +
      t
        .map(
          (u) =>
            `<span class="adm-si-chip"><b>${esc(userName(u.user_id) || u.email || 'Unknown')}</b> ` +
            `${u.logins} sign-in${u.logins === 1 ? '' : 's'}${u.restores ? ` · ${u.restores} resumed` : ''}</span>`
        )
        .join('') +
      '</div>'
    : '';
  list.innerHTML = rows.length
    ? rows
        .slice(0, 60)
        .map((r) => {
          const when = r.logged_at ? new Date(r.logged_at).toLocaleString() : '—';
          const dev = /mobile|android|iphone/i.test(r.user_agent || '') ? 'phone' : 'computer';
          return (
            '<div class="adm-si-row">' +
            `<b>${esc(userName(r.user_id) || r.email || 'Unknown')}</b>` +
            `<span class="adm-si-kind${r.kind === 'restore' ? ' mut' : ''}">${r.kind === 'restore' ? 'resumed session' : 'signed in'}</span>` +
            `<span class="adm-si-when">${esc(when)}</span>` +
            `<span class="adm-si-dev">${dev}</span>` +
            '</div>'
          );
        })
        .join('')
    : '<p class="adm-sub-h">No sign-ins recorded yet — entries appear from the next time anyone opens the app.</p>';
}

function renderAll() {
  renderEnforcement();
  renderSecurity();
  renderTeam();
  renderActorFilter();
  renderAudit();
  renderSignIns();
  renderErrors();
}

async function load() {
  // The route is already MANAGE-tiered in registry.js; this is defence in depth
  // for a directly-typed #/admin on a stale UI slice.
  if (!isAdmin(role())) {
    const d = doc();
    const host = d && d.getElementById('admin-view');
    if (host) host.innerHTML = '<header class="view-head"><h1>Admin</h1></header><p class="adm-lede">This area is for administrators.</p>';
    return;
  }
  renderAll();
  const p = await probeRlsEnforcement();
  _probe = p.ok ? p.data : { checked: false, applied: false };
  renderEnforcement();
  await refreshFactors(); // R-021: this account's own MFA state
  const ap = await probeAdminUnenroll();
  _mfaAdminProbe = ap.ok ? ap.data : { checked: false, available: false };
  renderSecurity();
  await refreshTeam();
  renderSecurity(); // the recovery picker lists the team — repaint once it lands
  await refreshAudit();
  // §3b — best-effort: a missing table (migration not yet run) renders its own
  // honest note instead of reddening the whole console.
  const si = await loadSignIns();
  _signins = si.ok ? { rows: si.data, error: null } : { rows: [], error: si.error };
  renderSignIns();
  renderErrors(); // final pass: pick up anything logged during this load
}

/**
 * Register the Admin console, replacing the Phase-1 placeholder. Call once at
 * boot from main.js (and the dev harness).
 * @returns {void}
 */
export function registerAdmin() {
  registerView('admin', {
    render(host) {
      host.innerHTML = skeleton();
      wireOnce();
      renderAll();
    },
    load() {
      return load();
    },
  });
}

/** Exported for the dev harness only: jump straight to a user's offboard modal. @param {string} uid @returns {void} */
export function _devOpenOffboard(uid) {
  const d = doc();
  const el = d && d.querySelector(`[data-action="adm-toggle-active"][data-uid="${uid}"]`);
  if (el) /** @type {any} */ (el).click();
}
